import { inject, Injectable } from '@angular/core';
import {
  CookieConsentConfig,
  acceptedCategory,
  run as runCookieConsent,
} from 'vanilla-cookieconsent';
import { TRANSLATION } from '../i18n';

// Notice: Make sure the consent values match the required consent string for each tag in GTM
const SERVICES = {
  analytics: {
    'Google Analytics': 'CC_GoogleAnalytics',
    'MS Clarity': 'CC_MSClarity',
  },
} as const;

type ConsentType = (typeof SERVICES.analytics)[keyof typeof SERVICES.analytics];

function updateConsent(consent: ConsentType, status: 'denied' | 'granted') {
  if (status === 'granted') {
    // eslint-disable-next-line functional/immutable-data
    window.dataLayer.push({ event: consent });
  }
}

// Updates Google Consent Mode v2 state so GTM/GA4/Ads respect it natively
function updateGoogleConsent(
  category: GoogleConsentCategory,
  status: 'denied' | 'granted',
) {
  const consentMap: Record<GoogleConsentCategory, Record<string, string>> = {
    analytics: { analytics_storage: status },
    marketing: {
      ad_storage: status,
      ad_user_data: status,
      ad_personalization: status,
    },
    functional: { functionality_storage: status },
  };
  gtag('consent', 'update', consentMap[category]);
}

const GOOGLE_CONSENT_CATEGORIES = [
  'analytics',
  'marketing',
  'functional',
] as const;
type GoogleConsentCategory = (typeof GOOGLE_CONSENT_CATEGORIES)[number];

const COOKIE_CONSENT_CONFIG = {
  cookie: { useLocalStorage: true },
  hideFromBots: true,
  manageScriptTags: false,
  revision: 2, // bump this once consent or used services change
  mode: 'opt-in' as const,

  // Fires on page load (for returning users) and on first consent
  onConsent: ({ cookie }: { cookie: { categories: string[] } }) => {
    GOOGLE_CONSENT_CATEGORIES.forEach((cat) =>
      updateGoogleConsent(
        cat,
        cookie.categories.includes(cat) ? 'granted' : 'denied',
      ),
    );
  },

  // Fires when the user changes their preferences
  onChange: ({ changedCategories }: { changedCategories: string[] }) => {
    GOOGLE_CONSENT_CATEGORIES.filter((cat) =>
      changedCategories.includes(cat),
    ).forEach((cat) =>
      updateGoogleConsent(cat, acceptedCategory(cat) ? 'granted' : 'denied'),
    );
  },

  guiOptions: {
    consentModal: {
      layout: 'box wide' as const,
      position: 'bottom right' as const,
      equalWeightButtons: false,
      flipButtons: true,
    },
    preferencesModal: {
      layout: 'box' as const,
      equalWeightButtons: false,
      flipButtons: true,
    },
  },

  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      enabled: false,
      readOnly: false,
      autoClear: {
        // _ga|_gid - Google Analytics; _clck|_clsk - MS Clarity
        cookies: [{ name: /^(_ga|_gid|_clck|_clsk)/ }],
      },
      services: Object.fromEntries(
        Object.entries(SERVICES.analytics).map(([name, consentSlug]) => [
          consentSlug,
          {
            label: name,
            onAccept: () => updateConsent(consentSlug, 'granted'),
            onReject: () => updateConsent(consentSlug, 'denied'),
          },
        ]),
      ),
    },
    marketing: {
      enabled: false,
      readOnly: false,
      autoClear: {
        // _fbp|fr - Meta; IDE|test_cookie - Google Ads; smt_ - SmartEmailing
        cookies: [{ name: /^(_fbp|fr|IDE|test_cookie|smt_)/ }],
      },
    },
    functional: {
      enabled: false,
      readOnly: false,
      autoClear: {
        // vuid - Vimeo; soundcloud_ - SoundCloud
        cookies: [{ name: /^(vuid|soundcloud_)/ }],
      },
    },
  },
} satisfies Omit<CookieConsentConfig, 'language'>;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly lang = inject(TRANSLATION);

  async activateCookieConsent() {
    await runCookieConsent({
      ...COOKIE_CONSENT_CONFIG,
      language: {
        default: this.lang.langCode,
        translations: {
          [this.lang.langCode]: this.lang.cookies,
        },
      },
    });
  }
}
